import axios from 'axios';
import { axiosInstance } from '../App';
import {
    UserSessionDTO,
    SignHandoffRequestDTO,
    SignIncomingHandoffRequestDTO,
    SignIncomingHandoffResponseDTO,
    HandoffReceiptDTO,
    CashSummaryDTO,
    PaginatedCashEntriesDTO,
    PaginatedHandoffDetailsDTO,
    AuditRequestDTO,
    CashRegisterDTO,
    CashRegisterFinancialAccountDTO,
    SaveCashRegisterRequestDTO,
    AssignCashRegisterRequestDTO,
    CloseAndReleaseRequestDTO,
    CloseAndReleaseResponseDTO,
    TakeOverLocationRequestDTO,
    TakeOverLocationResponseDTO,
    AuthorizedDeskDTO,
} from '../dto/locationcash/LocationCashDTO';
import { SECURED_API_BASE } from './apiConfig';

const BASE = `${SECURED_API_BASE}/locationcash`;

export function getActiveSession(hospitalLocationId: number): Promise<UserSessionDTO> {
    return axiosInstance.get(`${BASE}/getActiveSession`, {
        params: { hospitalLocationId }
    }).then(r => r.data).catch(e => { throw e; });
}

export function assignUser(hospitalLocationId: number): Promise<UserSessionDTO> {
    return axiosInstance.post(`${BASE}/assignUser`, null, {
        params: { hospitalLocationId }
    }).then(r => r.data).catch(e => { throw e; });
}

export function takeOverLocation(body: TakeOverLocationRequestDTO): Promise<TakeOverLocationResponseDTO> {
    return axiosInstance.post(`${BASE}/takeOverLocation`, body)
        .then(r => r.data).catch(e => { throw e; });
}

export function releaseUser(hospitalLocationId: number): Promise<void> {
    return axiosInstance.post(`${BASE}/releaseUser`, null, {
        params: { hospitalLocationId }
    }).then(r => r.data).catch(e => { throw e; });
}

export function signHandoffAndRelease(body: SignHandoffRequestDTO): Promise<HandoffReceiptDTO> {
    return axiosInstance.post(`${BASE}/signHandoffAndRelease`, body)
        .then(r => r.data).catch(e => { throw e; });
}

/** Devuelve null si el puesto no tiene traspaso pendiente (404). Cualquier otro error se propaga. */
export function getPendingHandoffReceipt(hospitalLocationId: number): Promise<HandoffReceiptDTO | null> {
    return axiosInstance.get(`${BASE}/getPendingHandoffReceipt`, {
        params: { hospitalLocationId }
    }).then(r => r.data).catch(e => {
        if (axios.isAxiosError(e) && e.response?.status === 404) return null;
        throw e;
    });
}

export function signIncomingHandoff(body: SignIncomingHandoffRequestDTO): Promise<SignIncomingHandoffResponseDTO> {
    return axiosInstance.post(`${BASE}/signIncomingHandoff`, body)
        .then(r => r.data).catch(e => { throw e; });
}

export function getCashSummary(hospitalLocationId: number): Promise<CashSummaryDTO> {
    return axiosInstance.get(`${BASE}/getCashSummary`, {
        params: { hospitalLocationId }
    }).then(r => r.data).catch(e => { throw e; });
}

export function getCashEntries(
    hospitalLocationId: number,
    from: string,
    to: string,
    offset = 0,
    pageSize = 10
): Promise<PaginatedCashEntriesDTO> {
    return axiosInstance.get(`${BASE}/getCashEntries`, {
        params: { hospitalLocationId, from, to, offset, pageSize }
    }).then(r => r.data).catch(e => { throw e; });
}

export function getCashRegister(hospitalLocationId: number): Promise<CashRegisterDTO> {
    return axiosInstance.get(`${BASE}/getCashRegister`, {
        params: { hospitalLocationId }
    }).then(r => r.data).catch(e => { throw e; });
}

export function getAllCashRegisters(): Promise<CashRegisterDTO[]> {
    return axiosInstance.get(`${BASE}/getAllCashRegisters`)
        .then(r => r.data).catch(e => { throw e; });
}

export function getCashRegistersByPOS(posId: number): Promise<CashRegisterDTO[]> {
    return axiosInstance.get(`${BASE}/getCashRegistersByPOS`, {
        params: { posId }
    }).then(r => r.data).catch(e => { throw e; });
}

export function getAvailableCashRegisters(): Promise<CashRegisterDTO[]> {
    return axiosInstance.get(`${BASE}/getAvailableCashRegisters`)
        .then(r => r.data).catch(e => { throw e; });
}

export function getCashRegistersForAdministration(): Promise<CashRegisterDTO[]> {
    return axiosInstance.get(`${BASE}/admin`)
        .then(r => r.data).catch(e => { throw e; });
}

export function createCashRegister(body: SaveCashRegisterRequestDTO): Promise<CashRegisterDTO> {
    return axiosInstance.post(`${BASE}/admin`, body)
        .then(r => r.data).catch(e => { throw e; });
}

export function updateCashRegister(registerId: number, body: SaveCashRegisterRequestDTO): Promise<CashRegisterDTO> {
    return axiosInstance.put(`${BASE}/admin/${registerId}`, body)
        .then(r => r.data).catch(e => { throw e; });
}

export function assignCashRegisterToLocation(registerId: number, hospitalLocationId: number): Promise<CashRegisterDTO> {
    const body: AssignCashRegisterRequestDTO = { hospitalLocationId };
    return axiosInstance.put(`${BASE}/${registerId}/hospital-location`, body)
        .then(r => r.data).catch(e => { throw e; });
}

export function unassignCashRegisterFromLocation(registerId: number): Promise<void> {
    return axiosInstance.delete(`${BASE}/${registerId}/hospital-location`)
        .then(r => r.data).catch(e => { throw e; });
}

export function getCashRegisterFinancialAccounts(registerId: number): Promise<CashRegisterFinancialAccountDTO[]> {
    return axiosInstance.get(`${BASE}/${registerId}/financial-accounts`)
        .then(r => r.data).catch(e => { throw e; });
}

export function getCashRegisterFinancialAccountsByPaymentMethod(
    registerId: number, paymentMethodId: number
): Promise<CashRegisterFinancialAccountDTO[]> {
    return axiosInstance.get(`${BASE}/${registerId}/financial-accounts/by-payment-method`, {
        params: { paymentMethodId },
    }).then(r => r.data).catch(e => { throw e; });
}

export function associateCashRegisterFinancialAccount(
    registerId: number, financialAccountId: number, isDefault = false
): Promise<CashRegisterFinancialAccountDTO> {
    return axiosInstance.post(`${BASE}/${registerId}/financial-accounts`, {
        financialAccountId,
        isDefault,
    }).then(r => r.data).catch(e => { throw e; });
}

export function disassociateCashRegisterFinancialAccount(registerId: number, financialAccountId: number): Promise<void> {
    return axiosInstance.delete(`${BASE}/${registerId}/financial-accounts/${financialAccountId}`)
        .then(r => r.data).catch(e => { throw e; });
}

export function setDefaultCashRegisterFinancialAccount(registerId: number, financialAccountId: number): Promise<void> {
    return axiosInstance.put(`${BASE}/${registerId}/financial-accounts/${financialAccountId}/set-default`)
        .then(r => r.data).catch(e => { throw e; });
}

export function getCurrentUserId(): Promise<number> {
    return axiosInstance.get(`${BASE}/getCurrentUserId`)
        .then(r => r.data).catch(e => { throw e; });
}

export function closeAndRelease(body: CloseAndReleaseRequestDTO): Promise<CloseAndReleaseResponseDTO> {
    return axiosInstance.post(`${BASE}/closeAndRelease`, body)
        .then(r => r.data).catch(e => { throw e; });
}

export function getHandoffDetails(
    hospitalLocationId: number,
    offset = 0,
    pageSize = 20
): Promise<PaginatedHandoffDetailsDTO> {
    return axiosInstance.get(`${BASE}/getHandoffDetails`, {
        params: { hospitalLocationId, offset, pageSize }
    }).then(r => r.data).catch(e => { throw e; });
}

export function auditHandoffDetail(detailId: number, body: AuditRequestDTO): Promise<void> {
    return axiosInstance.put(`${BASE}/handoff-receipt-detail/${detailId}/audit`, body)
        .then(r => r.data).catch(e => { throw e; });
}

export function auditCashClosing(closingId: number, body: AuditRequestDTO): Promise<void> {
    return axiosInstance.put(`${BASE}/cash-closing/${closingId}/audit`, body)
        .then(r => r.data).catch(e => { throw e; });
}

export function getMyActiveSession(): Promise<UserSessionDTO | null> {
    return axiosInstance.get(`${BASE}/getMyActiveSession`)
        .then(r => r.status === 204 ? null : r.data)
        .catch(e => { throw e; });
}

export function getAuthorizedDesks(buildingId: number): Promise<AuthorizedDeskDTO[]> {
    return axiosInstance.get(`${BASE}/getAuthorizedDesks`, {
        params: { buildingId }
    }).then(r => r.data).catch(e => { throw e; });
}
